import type { RarityTier } from "../rarity.js";

/** Who wrote a message, as a chat row draws them. */
export interface ChatAuthor {
  name: string;
  avatarSrc?: string | null;
  /** The level band beside the name; `label` is what's read ("Level 12"). */
  level?: { tier: RarityTier; label: string };
  /** One of six name colors (see `chatNameTone`), so a busy room is easy to follow. */
  nameTone?: ChatNameTone;
  /** Staff and hosts carry a tag (`label`: HOST, MOD) and a marked row. */
  role?: { kind: "host" | "moderator"; label: string };
}

export type ChatNameTone = 1 | 2 | 3 | 4 | 5 | 6;

/** The six name colors: the theme's accent and rarity hues, eased toward its text color. */
export const CHAT_NAME_TONES: Record<ChatNameTone, string> = {
  1: "text-chat-name-1",
  2: "text-chat-name-2",
  3: "text-chat-name-3",
  4: "text-chat-name-4",
  5: "text-chat-name-5",
  6: "text-chat-name-6",
};

/** A stable name color for a user who hasn't picked one: the same name always gets the same. */
export function chatNameTone(name: string): ChatNameTone {
  let hash = 0;
  for (const char of name) hash = (hash * 31 + (char.codePointAt(0) ?? 0)) | 0;
  return ((Math.abs(hash) % 6) + 1) as ChatNameTone;
}

/**
 * How the rows are drawn. `stream`: one line per message, the name and text inline, as live-stream
 * chats draw it. `card`: a filled block per message, the name above the text. The theme picks
 * (`layout.chatRows`); a surface can override it.
 */
export type ChatRowStyle = "stream" | "card";

/** Where the chat sits: its own panel, or straight over a stream or room art. */
export type ChatSurface = "panel" | "overlay";
